import { ConfigProvider, Layout } from "antd";
import { CaretDownOutlined, CaretRightOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import "./SideBar.scss";
import { NewModButtonsList } from "../mods/NewModButtons/NewModButtonsList";
import { ModsList } from "../mods/ModsList";
import { ModDetailsPanel } from "../mods/ModDetailsPanel";
import { useMst } from "../../stores/main";

const { Sider } = Layout;

const PANEL_HEADER_HEIGHT = 36;
const MIN_WIDTH = 235;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 360;
const DEFAULT_WIDTH_RATIO = 0.24;
const MAX_DEFAULT_WIDTH = 420;
const SIDEBAR_WIDTH_KEY = "playgroundSidebarWidth";
const KEEP_LAYER_SELECTION_SELECTOR = "[data-layer-inspector-keep-selection]";

const getLayerInspectorPopupContainer = (triggerNode?: HTMLElement) => {
  const protectedContainer = triggerNode?.closest(
    KEEP_LAYER_SELECTION_SELECTOR
  );

  if (protectedContainer instanceof HTMLElement) {
    return protectedContainer;
  }

  return document.body;
};

const getDefaultSidebarWidth = () => {
  if (typeof window === "undefined") {
    return DEFAULT_WIDTH;
  }

  return Math.max(
    MIN_WIDTH,
    Math.min(
      MAX_DEFAULT_WIDTH,
      Math.round(window.innerWidth * DEFAULT_WIDTH_RATIO)
    )
  );
};

export const SideBar = observer(function SideBar() {
  const {
    layoutStore: { connectorsPanelCollapsed, setConnectorsPanelCollapsed },
    modsStore: { activeModId, setActiveMod },
  } = useMst();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const hasActiveMod = activeModId !== "";

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    const storedWidth = stored ? parseInt(stored, 10) : NaN;
    return Number.isFinite(storedWidth)
      ? storedWidth
      : getDefaultSidebarWidth();
  });
  const [isResizing, setIsResizing] = useState(false);
  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const openConnectors = useCallback(() => {
    setConnectorsPanelCollapsed(false);
  }, [setConnectorsPanelCollapsed]);

  const handleClosePanel = useCallback(() => {
    setActiveMod(undefined);
  }, [setActiveMod]);

  const effectiveConnectorsCollapsed =
    connectorsPanelCollapsed && !hasActiveMod;

  const toggleConnectors = useCallback(() => {
    if (effectiveConnectorsCollapsed) {
      openConnectors();
      return;
    }

    setActiveMod(undefined);
    setConnectorsPanelCollapsed(true);
  }, [
    effectiveConnectorsCollapsed,
    openConnectors,
    setActiveMod,
    setConnectorsPanelCollapsed,
  ]);

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isResizingRef.current = true;
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = sidebarWidth;
      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";
    },
    [sidebarWidth]
  );

  const handleResizeMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizingRef.current) return;
    const delta = e.clientX - startXRef.current;
    const newWidth = Math.max(
      MIN_WIDTH,
      Math.min(MAX_WIDTH, startWidthRef.current + delta)
    );
    setSidebarWidth(newWidth);
  }, []);

  const handleResizeMouseUp = useCallback(() => {
    if (!isResizingRef.current) return;
    isResizingRef.current = false;
    setIsResizing(false);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  useEffect(() => {
    document.addEventListener("mousemove", handleResizeMouseMove);
    document.addEventListener("mouseup", handleResizeMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleResizeMouseMove);
      document.removeEventListener("mouseup", handleResizeMouseUp);
    };
  }, [handleResizeMouseMove, handleResizeMouseUp]);

  const containerStyle = {
    gridTemplateRows: effectiveConnectorsCollapsed
      ? `${PANEL_HEADER_HEIGHT}px`
      : "1fr",
    ["--sidebar-panel-header-height" as string]: `${PANEL_HEADER_HEIGHT}px`,
  };

  return (
    <ConfigProvider getPopupContainer={getLayerInspectorPopupContainer}>
      <div
        className={`sidebar-with-details-panel${
          effectiveConnectorsCollapsed ? " is-connectors-collapsed" : ""
        }`}
        data-layer-inspector-keep-selection
      >
        <Sider
          width={sidebarWidth}
          className={`playground-left-sidebar${
            isResizing ? " is-resizing" : ""
          }`}
        >
          <div
            className="playground-left-sidebar-inner-container playground-left-sidebar-inner-container--single-panel"
            ref={sidebarRef}
            style={containerStyle}
          >
            <section
              className={`sidebar-panel connectors-panel${
                effectiveConnectorsCollapsed ? " is-collapsed" : ""
              }`}
            >
              <div className="sidebar-panel-header">
                <div className="panel-title-group">
                  <button
                    type="button"
                    className="panel-toggle"
                    onClick={toggleConnectors}
                    aria-expanded={!effectiveConnectorsCollapsed}
                    aria-controls="connectors-panel-body"
                    title={
                      effectiveConnectorsCollapsed
                        ? "Expand connectors"
                        : "Collapse connectors"
                    }
                  >
                    {effectiveConnectorsCollapsed ? (
                      <CaretRightOutlined />
                    ) : (
                      <CaretDownOutlined />
                    )}
                  </button>
                  <span className="panel-title">Connectors</span>
                </div>
                <div className="panel-actions">
                  <NewModButtonsList
                    activeView="connectors"
                    onConnectorMenuOpen={openConnectors}
                    showViewToggle={false}
                  />
                </div>
              </div>
              <div className="sidebar-panel-body" id="connectors-panel-body">
                <ModsList />
              </div>
            </section>
          </div>
          <div
            className="sidebar-resize-handle"
            onMouseDown={handleResizeMouseDown}
          />
        </Sider>

        {/* Details panel - opens to the right of the sidebar */}
        <ModDetailsPanel onClose={handleClosePanel} />
      </div>
    </ConfigProvider>
  );
});
